import type { CSSProperties, ReactNode } from "react";
import { useEffect } from "react";

import { MazeAppShell } from "@/components/layout/maze-app-shell";
import { MazeToaster } from "@/components/maze-toaster";
import { OfflineStatusModal } from "@/components/molecules/offline-status-modal";
import { PageLoader } from "@/components/shared/PageLoader";
import { AuthGateScreen } from "@/components/shared/AuthGateScreen";
import { useAssetPreload } from "@/hooks/use-asset-preload";
import { useStageBootstrap } from "@/hooks/use-stage-bootstrap";
import { useDevAuth } from "@/hooks/use-dev-auth";
import { I18nProvider } from "@/lib/i18n/i18n-provider";
import { APP_COLOR } from "@/theme/color";
import { getConfiguredUiStageId } from "@/utils/stage/stage-utils";

interface AppProvidersProps {
  readonly children: ReactNode;
}

const bodyStyle: CSSProperties = {
  background: APP_COLOR.background,
  color: APP_COLOR.white,
  "--app-background": APP_COLOR.background,
  "--app-foreground": APP_COLOR.accent,
  "--game-accent-rgb": APP_COLOR.accentRgb,
} as CSSProperties;

function AppProvidersInner({ children }: AppProvidersProps) {
  const { isReady, error } = useDevAuth();
  const { status: assetPreloadStatus, retry: retryAssetPreload } = useAssetPreload();
  const stage = getConfiguredUiStageId();
  useStageBootstrap(isReady);

  // Mirrors these on <html> (not just the app wrapper div) so portaled
  // modals (createPortal → document.body) can also read them via var().
  useEffect(() => {
    const rootStyle = document.documentElement.style;
    rootStyle.setProperty("--game-bg", APP_COLOR.background);
    rootStyle.setProperty("--game-eclipse", APP_COLOR.eclipse);
    rootStyle.setProperty("--game-accent", APP_COLOR.accent);
    rootStyle.setProperty("--game-accent-rgb", APP_COLOR.accentRgb);
  }, []);

  if (error) {
    return <AuthGateScreen gameThemeStyle={bodyStyle} error={error} />;
  }

  if (assetPreloadStatus !== 'ready') {
    return (
      <MazeAppShell>
        <PageLoader />
        <OfflineStatusModal
          isOpen={assetPreloadStatus === 'offline'}
          stage={stage}
          onRetry={retryAssetPreload}
        />
      </MazeAppShell>
    );
  }

  return (
    <MazeAppShell>
      {children}
      <MazeToaster />
    </MazeAppShell>
  );
}

export function AppProviders({ children }: AppProvidersProps) {
  return (
    <I18nProvider>
      <div
        className="antialiased min-h-dvh w-full select-none"
        style={bodyStyle}
      >
        <AppProvidersInner>{children}</AppProvidersInner>
      </div>
    </I18nProvider>
  );
}
